import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useStore, selectBusinesses, selectProjectsByBusiness } from '@/utils/store';
import { CATEGORY_ICONS, formatCurrency as fmtCurrency } from '@/utils/format';
import { useCurrency } from '@/hooks/useCurrency';
import PageHeader from '@/components/shared/PageHeader';
import AddBusinessModal from '@/components/shared/AddBusinessModal';
import AddProjectModal from '@/components/shared/AddProjectModal';
import AddTransactionModal from '@/components/shared/AddTransactionModal';
import ExchangeRateBadge from '@/components/shared/ExchangeRateBadge';
import CollaboratorsPanel from '@/components/shared/CollaboratorsPanel';
import ExchangeRateModal from '@/components/shared/ExchangeRateModal';
import ViewToggle from '@/components/ui/ViewToggle';
import { Plus, ChevronLeft, ChevronRight, Trash2, Pencil, ArrowLeft, CheckCircle2, Circle, ArrowRightLeft, Copy } from 'lucide-react';

const STATUS_COLORS = {
  active: 'bg-success-100 text-success-700 dark:bg-success-500/10 dark:text-success-400',
  completed: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
  paused: 'bg-warning-100 text-warning-700 dark:bg-warning-500/10 dark:text-warning-400',
};

export default function Business() {
  const { t } = useTranslation();
  const { format: formatCurrency } = useCurrency();
  const businesses = useStore(selectBusinesses);
  const transactions = useStore((s) => s.transactions);
  const removeBusiness = useStore((s) => s.removeBusiness);
  const duplicateBusiness = useStore((s) => s.duplicateBusiness);
  const removeProject = useStore((s) => s.removeProject);
  const updateMilestone = useStore((s) => s.updateMilestone);

  const [modalOpen, setModalOpen] = useState(false);
  const [editBusiness, setEditBusiness] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [monthOffset, setMonthOffset] = useState(0);

  // Project state
  const [projectModalOpen, setProjectModalOpen] = useState(false);
  const [editProject, setEditProject] = useState(null);
  const [selectedProjectId, setSelectedProjectId] = useState(null);

  // Transaction modal state
  const [txModalOpen, setTxModalOpen] = useState(false);
  const [txDefaultType, setTxDefaultType] = useState('expense');
  const [txDefaultProjectId, setTxDefaultProjectId] = useState('');
  const [editingTx, setEditingTx] = useState(null);

  // Exchange modal state
  const [exchangeModalOpen, setExchangeModalOpen] = useState(false);

  // View mode
  const [projectViewMode, setProjectViewMode] = useState('list');

  const now = new Date();
  const viewDate = new Date(now.getFullYear(), now.getMonth() + monthOffset, 1);
  const monthKey = viewDate.toISOString().slice(0, 7);
  const monthLabel = viewDate.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });

  const shiftMonth = (dir) => setMonthOffset((p) => p + dir);

  const user = useStore((s) => s.user);
  const selected = businesses.find((b) => b.id === selectedId);
  const projects = useStore(selectProjectsByBusiness(selectedId || ''));
  const selectedProject = projects.find((p) => p.id === selectedProjectId);
  const fmtBiz = (amount) => selected?.currency ? fmtCurrency(amount, selected.currency) : formatCurrency(amount);
  const showExchange = selected?.currency && selected.currency !== (user.currency || 'CLP');

  const bizTransactions = selected
    ? transactions.filter((tx) => tx.businessId === selected.id && tx.date?.startsWith(monthKey))
    : [];

  const bizIncome = bizTransactions.filter((tx) => tx.type === 'income').reduce((s, tx) => s + tx.amount, 0);
  const bizExpenses = bizTransactions.filter((tx) => tx.type === 'expense').reduce((s, tx) => s + tx.amount, 0);
  const bizBalance = bizIncome - bizExpenses;

  const handleEdit = (biz) => {
    setEditBusiness(biz);
    setModalOpen(true);
  };

  const handleAdd = () => {
    setEditBusiness(null);
    setModalOpen(true);
  };

  const handleDelete = (id) => {
    removeBusiness(id);
    if (selectedId === id) setSelectedId(null);
  };

  const handleAddProject = () => {
    setEditProject(null);
    setProjectModalOpen(true);
  };

  const handleEditProject = (proj) => {
    setEditProject(proj);
    setProjectModalOpen(true);
  };

  const handleToggleMilestone = (projectId, milestoneId, currentPaid) => {
    updateMilestone(projectId, milestoneId, { paid: !currentPaid });
  };

  const openTxModal = (type, projId = '') => {
    setTxDefaultType(type);
    setTxDefaultProjectId(projId);
    setTxModalOpen(true);
  };

  // Project detail view
  if (selected && selectedProject) {
    const projTx = transactions.filter((tx) => tx.projectId === selectedProject.id && tx.date?.startsWith(monthKey));
    const projIncome = projTx.filter((tx) => tx.type === 'income').reduce((s, tx) => s + tx.amount, 0);
    const projExpenses = projTx.filter((tx) => tx.type === 'expense').reduce((s, tx) => s + tx.amount, 0);
    const collected = (selectedProject.milestones || []).filter((m) => m.paid).reduce((s, m) => s + m.amount, 0);
    const pendingAmount = (selectedProject.budget || 0) - collected;

    return (
      <div className="space-y-4 animate-fade-in">
        <button
          onClick={() => setSelectedProjectId(null)}
          className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 hover:text-brand-600 dark:hover:text-brand-400 transition-colors"
        >
          <ArrowLeft size={16} />
          {selected.name} / {t('projects.title')}
        </button>

        {/* Project header */}
        <div className="flex items-center gap-3">
          <div
            className="w-12 h-12 rounded-2xl flex items-center justify-center text-2xl"
            style={{ backgroundColor: selectedProject.color + '20' }}
          >
            {selectedProject.icon}
          </div>
          <div className="flex-1">
            <h1 className="text-xl font-display font-bold text-gray-900 dark:text-white">{selectedProject.name}</h1>
            <div className="flex items-center gap-2">
              {selectedProject.client && (
                <span className="text-sm text-gray-500 dark:text-gray-400">{selectedProject.client}</span>
              )}
              <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-md ${STATUS_COLORS[selectedProject.status]}`}>
                {t(`projects.status${selectedProject.status.charAt(0).toUpperCase() + selectedProject.status.slice(1)}`)}
              </span>
            </div>
            {(selectedProject.startDate || selectedProject.endDate) && (
              <div className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                {selectedProject.startDate} → {selectedProject.endDate || '...'}
              </div>
            )}
          </div>
          <button
            onClick={() => handleEditProject(selectedProject)}
            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-400 transition-colors"
          >
            <Pencil size={16} />
          </button>
        </div>

        {/* Summary cards */}
        <div className="grid grid-cols-2 gap-3">
          <div className="card text-center py-3">
            <div className="text-xs text-gray-500 dark:text-gray-400">{t('projects.budget')}</div>
            <div className="text-lg font-bold text-gray-900 dark:text-white">{fmtBiz(selectedProject.budget || 0)}</div>
          </div>
          <div className="card text-center py-3">
            <div className="text-xs text-gray-500 dark:text-gray-400">{t('projects.collected')}</div>
            <div className="text-lg font-bold text-success-600 dark:text-success-400">{fmtBiz(collected)}</div>
          </div>
          <div className="card text-center py-3">
            <div className="text-xs text-gray-500 dark:text-gray-400">{t('projects.pending')}</div>
            <div className="text-lg font-bold text-warning-600 dark:text-warning-400">{fmtBiz(pendingAmount > 0 ? pendingAmount : 0)}</div>
          </div>
          <div className="card text-center py-3">
            <div className="text-xs text-gray-500 dark:text-gray-400">{t('projects.expenses')}</div>
            <div className="text-lg font-bold text-danger-600 dark:text-danger-400">{fmtBiz(projExpenses)}</div>
          </div>
        </div>

        {/* Quick add transaction buttons */}
        <div className="flex gap-2">
          <button
            onClick={() => openTxModal('income', selectedProject.id)}
            className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl bg-success-50 dark:bg-success-500/10 text-success-700 dark:text-success-400 text-sm font-medium hover:bg-success-100 dark:hover:bg-success-500/20 transition-colors border border-success-200 dark:border-success-500/20"
          >
            <Plus size={16} />
            {t('common.income')}
          </button>
          <button
            onClick={() => openTxModal('expense', selectedProject.id)}
            className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl bg-danger-50 dark:bg-danger-500/10 text-danger-700 dark:text-danger-400 text-sm font-medium hover:bg-danger-100 dark:hover:bg-danger-500/20 transition-colors border border-danger-200 dark:border-danger-500/20"
          >
            <Plus size={16} />
            {t('common.expense')}
          </button>
        </div>

        {/* Milestones */}
        <div>
          <h2 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">{t('projects.milestones')}</h2>
          {(!selectedProject.milestones || selectedProject.milestones.length === 0) ? (
            <div className="card text-center py-6 text-sm text-gray-400 dark:text-gray-500">
              {t('projects.noMilestones')}
            </div>
          ) : (
            <div className="space-y-2">
              {selectedProject.milestones.map((m) => (
                <div
                  key={m.id}
                  className={`card flex items-center gap-3 py-3 px-4 cursor-pointer transition-colors ${m.paid ? 'opacity-70' : ''}`}
                  onClick={() => handleToggleMilestone(selectedProject.id, m.id, m.paid)}
                >
                  {m.paid ? (
                    <CheckCircle2 size={20} className="text-success-500 shrink-0" />
                  ) : (
                    <Circle size={20} className="text-gray-300 dark:text-gray-600 shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className={`text-sm font-medium ${m.paid ? 'line-through text-gray-400' : 'text-gray-900 dark:text-white'}`}>
                      {m.name}
                    </div>
                    {m.dueDate && (
                      <div className="text-xs text-gray-400 dark:text-gray-500">{m.dueDate}</div>
                    )}
                  </div>
                  <span className={`text-sm font-bold ${m.paid ? 'text-success-600 dark:text-success-400' : 'text-gray-900 dark:text-white'}`}>
                    {fmtBiz(m.amount)}
                  </span>
                </div>
              ))}
            </div>
          )}
          {/* Progress bar */}
          {selectedProject.budget > 0 && (
            <div className="mt-3">
              <div className="flex justify-between text-[10px] text-gray-400 mb-1">
                <span>{Math.round((collected / selectedProject.budget) * 100)}%</span>
                <span>{fmtBiz(collected)} / {fmtBiz(selectedProject.budget)}</span>
              </div>
              <div className="h-2 bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden">
                <div
                  className="h-full bg-success-500 rounded-full transition-all"
                  style={{ width: `${Math.min(100, (collected / selectedProject.budget) * 100)}%` }}
                />
              </div>
            </div>
          )}
        </div>

        {/* Month nav for transactions */}
        <div className="flex items-center justify-between">
          <button onClick={() => shiftMonth(-1)} className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-500 transition-colors">
            <ChevronLeft size={20} />
          </button>
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300 capitalize">{monthLabel}</span>
          <button onClick={() => shiftMonth(1)} className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-500 transition-colors">
            <ChevronRight size={20} />
          </button>
        </div>

        {/* Transactions */}
        <div>
          <h2 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">{t('projects.transactions')}</h2>
          {projTx.length === 0 ? (
            <div className="card text-center py-6 text-sm text-gray-400 dark:text-gray-500">
              {t('projects.noTransactions')}
            </div>
          ) : (
            <div className="space-y-2">
              {projTx.map((tx) => (
                <div key={tx.id} onClick={() => setEditingTx(tx)} className="card flex items-center gap-3 py-3 px-4 cursor-pointer hover:border-brand-300 dark:hover:border-brand-600 transition-colors">
                  <span className="text-lg">{CATEGORY_ICONS[tx.category] || '📌'}</span>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-gray-900 dark:text-white truncate">{tx.description}</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">{t(`categories.${tx.category}`)} · {tx.date}</div>
                  </div>
                  <span className={`text-sm font-bold ${tx.type === 'income' ? 'text-success-600 dark:text-success-400' : 'text-danger-600 dark:text-danger-400'}`}>
                    {tx.type === 'income' ? '+' : '-'}{fmtBiz(tx.amount)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        <AddProjectModal open={projectModalOpen} onClose={() => setProjectModalOpen(false)} project={editProject} businessId={selected.id} />
        <AddTransactionModal open={txModalOpen} onClose={() => setTxModalOpen(false)} defaultType={txDefaultType} defaultBusinessId={selected.id} defaultProjectId={txDefaultProjectId} />
        <AddTransactionModal open={!!editingTx} onClose={() => setEditingTx(null)} transaction={editingTx} />
      </div>
    );
  }

  // Detail view for a selected business
  if (selected) {
    return (
      <div className="space-y-4 animate-fade-in">
        <button
          onClick={() => setSelectedId(null)}
          className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 hover:text-brand-600 dark:hover:text-brand-400 transition-colors"
        >
          <ArrowLeft size={16} />
          {t('business.title')}
        </button>

        {/* Business header */}
        <div className="flex items-center gap-3">
          <div
            className="w-12 h-12 rounded-2xl flex items-center justify-center text-2xl"
            style={{ backgroundColor: selected.color + '20' }}
          >
            {selected.icon}
          </div>
          <div className="flex-1">
            <h1 className="text-xl font-display font-bold text-gray-900 dark:text-white">{selected.name}</h1>
            <div className="flex items-center gap-2">
              {selected.description && (
                <span className="text-sm text-gray-500 dark:text-gray-400">{selected.description}</span>
              )}
              {selected.currency && (
                <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-md bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400">
                  {selected.currency}
                </span>
              )}
            </div>
          </div>
          <button
            onClick={() => handleEdit(selected)}
            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-400 transition-colors"
          >
            <Pencil size={16} />
          </button>
        </div>

        {/* Month nav */}
        <div className="flex items-center justify-between">
          <button onClick={() => shiftMonth(-1)} className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-500 transition-colors">
            <ChevronLeft size={20} />
          </button>
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300 capitalize">{monthLabel}</span>
          <button onClick={() => shiftMonth(1)} className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-500 transition-colors">
            <ChevronRight size={20} />
          </button>
        </div>

        {/* Totals */}
        <div className="grid grid-cols-3 gap-3">
          <div className="card text-center py-3">
            <div className="text-xs text-gray-500 dark:text-gray-400">{t('business.totalIncome')}</div>
            <div className="text-lg font-bold text-success-600 dark:text-success-400">{fmtBiz(bizIncome)}</div>
            {showExchange && bizIncome > 0 && (
              <ExchangeRateBadge amount={bizIncome} from={selected.currency} to={user.currency || 'CLP'} />
            )}
          </div>
          <div className="card text-center py-3">
            <div className="text-xs text-gray-500 dark:text-gray-400">{t('business.totalExpenses')}</div>
            <div className="text-lg font-bold text-danger-600 dark:text-danger-400">{fmtBiz(bizExpenses)}</div>
          </div>
          <div className="card text-center py-3">
            <div className="text-xs text-gray-500 dark:text-gray-400">{t('business.balance')}</div>
            <div className={`text-lg font-bold ${bizBalance >= 0 ? 'text-success-600 dark:text-success-400' : 'text-danger-600 dark:text-danger-400'}`}>
              {fmtBiz(bizBalance)}
            </div>
            {showExchange && bizBalance !== 0 && (
              <ExchangeRateBadge amount={bizBalance} from={selected.currency} to={user.currency || 'CLP'} />
            )}
          </div>
        </div>

        {/* Quick add transaction buttons */}
        <div className="flex gap-2">
          <button
            onClick={() => openTxModal('income')}
            className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl bg-success-50 dark:bg-success-500/10 text-success-700 dark:text-success-400 text-sm font-medium hover:bg-success-100 dark:hover:bg-success-500/20 transition-colors border border-success-200 dark:border-success-500/20"
          >
            <Plus size={16} />
            {t('common.income')}
          </button>
          <button
            onClick={() => openTxModal('expense')}
            className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl bg-danger-50 dark:bg-danger-500/10 text-danger-700 dark:text-danger-400 text-sm font-medium hover:bg-danger-100 dark:hover:bg-danger-500/20 transition-colors border border-danger-200 dark:border-danger-500/20"
          >
            <Plus size={16} />
            {t('common.expense')}
          </button>
        </div>

        {/* Exchange button */}
        {showExchange && (
          <button
            onClick={() => setExchangeModalOpen(true)}
            className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl bg-brand-50 dark:bg-brand-500/10 text-brand-700 dark:text-brand-400 text-sm font-medium hover:bg-brand-100 dark:hover:bg-brand-500/20 transition-colors border border-brand-200 dark:border-brand-500/20"
          >
            <ArrowRightLeft size={16} />
            {t('exchange.registerExchange')} ({selected.currency} → {user.currency || 'CLP'})
          </button>
        )}

        {/* Collaborators */}
        <CollaboratorsPanel resourceType="business" resourceId={selected.id} />

        {/* Projects section */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <h2 className="text-sm font-medium text-gray-500 dark:text-gray-400">{t('projects.title')}</h2>
              <span className="text-xs text-gray-400 font-medium">{projects.length}</span>
            </div>
            <div className="flex items-center gap-2">
              <ViewToggle view={projectViewMode} onChange={setProjectViewMode} />
              <button
                onClick={handleAddProject}
                className="flex items-center gap-1 text-xs font-medium text-brand-600 dark:text-brand-400 hover:text-brand-700 transition-colors"
              >
                <Plus size={14} />
                {t('projects.addNew')}
              </button>
            </div>
          </div>
          {projects.length === 0 ? (
            <div className="card text-center py-6 text-sm text-gray-400 dark:text-gray-500">
              {t('projects.noProjects')}
            </div>
          ) : projectViewMode === 'list' ? (
            <div className="space-y-2">
              {projects.map((proj) => {
                const collected = (proj.milestones || []).filter((m) => m.paid).reduce((s, m) => s + m.amount, 0);
                const pct = proj.budget > 0 ? Math.round((collected / proj.budget) * 100) : 0;

                return (
                  <div
                    key={proj.id}
                    className="card hover:border-brand-300 dark:hover:border-brand-600 transition-colors cursor-pointer group relative"
                    onClick={() => setSelectedProjectId(proj.id)}
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className="w-10 h-10 rounded-xl flex items-center justify-center text-lg"
                        style={{ backgroundColor: proj.color + '20' }}
                      >
                        {proj.icon}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-gray-900 dark:text-white text-sm">{proj.name}</span>
                          <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-md ${STATUS_COLORS[proj.status]}`}>
                            {t(`projects.status${proj.status.charAt(0).toUpperCase() + proj.status.slice(1)}`)}
                          </span>
                        </div>
                        {proj.client && (
                          <span className="text-xs text-gray-500 dark:text-gray-400">{proj.client}</span>
                        )}
                        {proj.budget > 0 && (
                          <div className="mt-1.5">
                            <div className="h-1.5 bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden">
                              <div
                                className="h-full bg-success-500 rounded-full transition-all"
                                style={{ width: `${Math.min(100, pct)}%` }}
                              />
                            </div>
                            <div className="flex justify-between text-[10px] text-gray-400 mt-0.5">
                              <span>{fmtBiz(collected)} / {fmtBiz(proj.budget)}</span>
                              <span>{pct}%</span>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Hover actions */}
                    <div className="absolute top-2 right-2 hidden group-hover:flex gap-1">
                      <button
                        onClick={(e) => { e.stopPropagation(); handleEditProject(proj); }}
                        className="p-1.5 rounded-lg bg-gray-100 dark:bg-gray-800 text-gray-500 hover:text-brand-600 transition-colors"
                      >
                        <Pencil size={14} />
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); removeProject(proj.id); }}
                        className="p-1.5 rounded-lg bg-gray-100 dark:bg-gray-800 text-gray-500 hover:text-danger-500 transition-colors"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="flex gap-3 overflow-x-auto pb-2 -mx-4 px-4 md:mx-0 md:px-0">
              {[
                { key: 'active', label: t('projects.statusActive'), items: projects.filter((p) => p.status === 'active'), colorClass: STATUS_COLORS.active },
                { key: 'paused', label: t('projects.statusPaused'), items: projects.filter((p) => p.status === 'paused'), colorClass: STATUS_COLORS.paused },
                { key: 'completed', label: t('projects.statusCompleted'), items: projects.filter((p) => p.status === 'completed'), colorClass: STATUS_COLORS.completed },
              ].map((col) => (
                <div key={col.key} className="flex-shrink-0 w-72 md:flex-1 md:min-w-0">
                  <div className="flex items-center gap-2 mb-3 px-1">
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-md ${col.colorClass}`}>
                      {col.label}
                    </span>
                    <span className="text-xs text-gray-400 font-medium">{col.items.length}</span>
                  </div>
                  <div className="space-y-2 min-h-[100px] bg-gray-50 dark:bg-gray-900/50 rounded-xl p-2">
                    {col.items.length === 0 ? (
                      <div className="text-center py-8 text-xs text-gray-400 dark:text-gray-500">
                        {t('common.columnEmpty')}
                      </div>
                    ) : (
                      col.items.map((proj) => {
                        const collected = (proj.milestones || []).filter((m) => m.paid).reduce((s, m) => s + m.amount, 0);
                        const pct = proj.budget > 0 ? Math.round((collected / proj.budget) * 100) : 0;

                        return (
                          <div
                            key={proj.id}
                            className="card hover:border-brand-300 dark:hover:border-brand-600 transition-colors cursor-pointer group relative"
                            onClick={() => setSelectedProjectId(proj.id)}
                          >
                            <div className="flex items-center gap-3">
                              <div
                                className="w-10 h-10 rounded-xl flex items-center justify-center text-lg"
                                style={{ backgroundColor: proj.color + '20' }}
                              >
                                {proj.icon}
                              </div>
                              <div className="flex-1 min-w-0">
                                <span className="font-semibold text-gray-900 dark:text-white text-sm">{proj.name}</span>
                                {proj.client && (
                                  <div className="text-xs text-gray-500 dark:text-gray-400">{proj.client}</div>
                                )}
                                {proj.budget > 0 && (
                                  <div className="mt-1.5">
                                    <div className="h-1.5 bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden">
                                      <div
                                        className="h-full bg-success-500 rounded-full transition-all"
                                        style={{ width: `${Math.min(100, pct)}%` }}
                                      />
                                    </div>
                                    <div className="flex justify-between text-[10px] text-gray-400 mt-0.5">
                                      <span>{fmtBiz(collected)} / {fmtBiz(proj.budget)}</span>
                                      <span>{pct}%</span>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>

                            {/* Hover actions */}
                            <div className="absolute top-2 right-2 hidden group-hover:flex gap-1">
                              <button
                                onClick={(e) => { e.stopPropagation(); handleEditProject(proj); }}
                                className="p-1.5 rounded-lg bg-gray-100 dark:bg-gray-800 text-gray-500 hover:text-brand-600 transition-colors"
                              >
                                <Pencil size={14} />
                              </button>
                              <button
                                onClick={(e) => { e.stopPropagation(); removeProject(proj.id); }}
                                className="p-1.5 rounded-lg bg-gray-100 dark:bg-gray-800 text-gray-500 hover:text-danger-500 transition-colors"
                              >
                                <Trash2 size={14} />
                              </button>
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Transactions list */}
        <div>
          <h2 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">{t('business.transactions')}</h2>
          {bizTransactions.length === 0 ? (
            <div className="card text-center py-8 text-sm text-gray-400 dark:text-gray-500">
              {t('business.noTransactions')}
            </div>
          ) : (
            <div className="space-y-2">
              {bizTransactions.map((tx) => (
                <div key={tx.id} onClick={() => setEditingTx(tx)} className="card flex items-center gap-3 py-3 px-4 cursor-pointer hover:border-brand-300 dark:hover:border-brand-600 transition-colors">
                  <span className="text-lg">{CATEGORY_ICONS[tx.category] || '📌'}</span>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-gray-900 dark:text-white truncate">{tx.description}</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      {t(`categories.${tx.category}`)} · {tx.date}
                      {tx.clientName && ` · ${tx.clientName}`}
                      {tx.supplierName && ` · ${tx.supplierName}`}
                      {tx.projectId && (() => {
                        const proj = projects.find((p) => p.id === tx.projectId);
                        return proj ? ` · ${proj.icon} ${proj.name}` : '';
                      })()}
                    </div>
                  </div>
                  <span className={`text-sm font-bold ${tx.type === 'income' ? 'text-success-600 dark:text-success-400' : 'text-danger-600 dark:text-danger-400'}`}>
                    {tx.type === 'income' ? '+' : '-'}{fmtBiz(tx.amount)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        <AddBusinessModal open={modalOpen} onClose={() => setModalOpen(false)} business={editBusiness} />
        <AddProjectModal open={projectModalOpen} onClose={() => setProjectModalOpen(false)} project={editProject} businessId={selected.id} />
        <AddTransactionModal open={txModalOpen} onClose={() => setTxModalOpen(false)} defaultType={txDefaultType} defaultBusinessId={selected.id} defaultProjectId={txDefaultProjectId} />
        <AddTransactionModal open={!!editingTx} onClose={() => setEditingTx(null)} transaction={editingTx} />
        <ExchangeRateModal open={exchangeModalOpen} onClose={() => setExchangeModalOpen(false)} business={selected} />
      </div>
    );
  }

  // Business list view
  return (
    <div className="space-y-4 animate-fade-in">
      <PageHeader
        title={t('business.title')}
        hint={t('common.hints.business')}
        action={
          <button
            onClick={handleAdd}
            className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-brand-600 text-white text-sm font-medium hover:bg-brand-700 transition-colors"
          >
            <Plus size={16} />
            {t('business.addNew')}
          </button>
        }
      />

      {businesses.length === 0 ? (
        <div className="card text-center py-12">
          <div className="text-4xl mb-3">💼</div>
          <p className="text-sm text-gray-500 dark:text-gray-400 max-w-xs mx-auto">
            {t('business.noBusiness')}
          </p>
          <button
            onClick={handleAdd}
            className="mt-4 px-4 py-2 rounded-xl bg-brand-600 text-white text-sm font-medium hover:bg-brand-700 transition-colors"
          >
            {t('business.addNew')}
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {businesses.map((biz) => {
            const bizTx = transactions.filter((tx) => tx.businessId === biz.id);
            const inc = bizTx.filter((tx) => tx.type === 'income').reduce((s, tx) => s + tx.amount, 0);
            const exp = bizTx.filter((tx) => tx.type === 'expense').reduce((s, tx) => s + tx.amount, 0);
            const fmtB = (amount) => biz.currency ? fmtCurrency(amount, biz.currency) : formatCurrency(amount);

            return (
              <div
                key={biz.id}
                className="card hover:border-brand-300 dark:hover:border-brand-600 transition-colors cursor-pointer group relative"
                onClick={() => setSelectedId(biz.id)}
              >
                <div className="flex items-center gap-3">
                  <div
                    className="w-11 h-11 rounded-xl flex items-center justify-center text-xl"
                    style={{ backgroundColor: biz.color + '20' }}
                  >
                    {biz.icon}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-gray-900 dark:text-white">{biz.name}</div>
                    <div className="flex items-center gap-1.5">
                      {biz.description && (
                        <span className="text-xs text-gray-500 dark:text-gray-400 truncate">{biz.description}</span>
                      )}
                      {biz.currency && (
                        <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-md bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 shrink-0">
                          {biz.currency}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      <span className="text-success-600 dark:text-success-400">+{fmtB(inc)}</span>
                      {' / '}
                      <span className="text-danger-600 dark:text-danger-400">-{fmtB(exp)}</span>
                    </div>
                  </div>
                </div>

                {/* Hover actions */}
                <div className="absolute top-2 right-2 hidden group-hover:flex gap-1">
                  <button
                    onClick={(e) => { e.stopPropagation(); duplicateBusiness(biz.id); }}
                    className="p-1.5 rounded-lg bg-gray-100 dark:bg-gray-800 text-gray-500 hover:text-brand-600 transition-colors"
                  >
                    <Copy size={14} />
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); handleEdit(biz); }}
                    className="p-1.5 rounded-lg bg-gray-100 dark:bg-gray-800 text-gray-500 hover:text-brand-600 transition-colors"
                  >
                    <Pencil size={14} />
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); handleDelete(biz.id); }}
                    className="p-1.5 rounded-lg bg-gray-100 dark:bg-gray-800 text-gray-500 hover:text-danger-500 transition-colors"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <AddBusinessModal open={modalOpen} onClose={() => setModalOpen(false)} business={editBusiness} />
    </div>
  );
}
